import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideSteps,
  GuideStep,
  GuideGrid,
  GuideCard,
  GuideDarkSection
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function WhatMakesAValidClaimGuide() {
  return (
    <GuideLayout
      slug="what-makes-a-valid-claim"
      category="Getting Started"
      categoryColor="brand"
      title="What Makes a Valid Pothole Claim?"
      description="Understand when you can claim compensation for pothole damage and what conditions need to be met for a successful claim."
      readTime="5 min"
      updatedDate="12 January 2026"
      reviewedFor="UK law"
      sources={[
        { name: 'legislation.gov.uk', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/41' },
      ]}
      relatedGuides={[
        { slug: 'who-is-responsible', title: 'Who Is Responsible for the Road?', readTime: '4 min' },
        { slug: 'section-41-explained', title: 'Section 41 of the Highways Act', readTime: '6 min' },
        { slug: 'photographing-pothole-damage', title: 'How to Photograph Pothole Damage', readTime: '6 min' },
      ]}
      atAGlance={[
        { label: 'Time limit to claim', value: '3 years from the date of the incident' },
        { label: 'Core evidence', value: 'Photos of the pothole and damage, exact location, itemised repair quote' },
        { label: 'Legal basis', value: 'Breach of the Section 41 duty to maintain (Highways Act 1980)' },
        { label: 'Defect threshold', value: 'Typically 40mm+ deep in the carriageway' },
      ]}
      howTo={{
        name: 'How to Check if You Have a Valid Pothole Claim',
        description:
          'Five steps to confirm you can claim compensation for pothole damage from a UK highway authority.',
        steps: [
          {
            name: 'Check the road type',
            text: "Make sure the incident happened on a publicly maintained road. Private roads, car parks, and unadopted roads don't qualify for claims against the council.",
          },
          {
            name: 'Assess the defect',
            text: 'Confirm the pothole was significant — generally 40mm or deeper in the carriageway. Take photos showing the depth if possible.',
          },
          {
            name: 'Document the damage',
            text: 'Get your vehicle inspected and obtain a repair quote or invoice. The garage should itemise what was damaged and the cost to fix each item.',
          },
          {
            name: 'Gather your evidence',
            text: 'Collect timestamped photos of the pothole showing depth and width with an object for scale, GPS-tagged photos or a map pin confirming the location, an itemised garage quote separating parts from labour, and an incident record covering date, time, speed, and weather conditions.',
          },
          {
            name: 'Check eligibility with Fixtyer',
            text: "Use Fixtyer's free check for a preliminary evidence review and a likely-authority lead. Verify the specific road manager and assess any third-party report leads against official records before deciding what to do.",
          },
        ],
      }}
      faqs={[
        {
          question: 'Can I claim for pothole damage on a private road?',
          answer: 'No, you can only claim against highway authorities for damage on publicly maintained roads. Private roads, car parks, and unadopted roads are the responsibility of the landowner, not the council. You would need to pursue a claim against the private landowner instead.',
        },
        {
          question: 'How deep does a pothole need to be to claim?',
          answer: 'Most councils use an "intervention level" of around 40mm (about 1.5 inches) to determine what constitutes a dangerous defect. Potholes shallower than this may still cause damage, but councils often argue they\'re within acceptable tolerances. The deeper and more severe the pothole, the stronger your claim.',
        },
        {
          question: 'What if I didn\'t get photos at the time of the incident?',
          answer: 'While photos taken immediately are ideal, you can still return to photograph the pothole later. If it\'s been repaired, photograph the repair patch. You can also request the council\'s inspection records and check FixMyStreet for prior reports that may include photos.',
        },
        {
          question: 'Can I claim for damage to a hire car or company vehicle?',
          answer: 'Yes, but you\'ll need to prove you\'re the one who suffered the financial loss. For hire cars, you may be liable for the damage under your rental agreement. For company vehicles, the registered keeper (usually the company) would typically need to make the claim.',
        },
        {
          question: 'Is there a minimum amount I can claim for?',
          answer: 'There\'s no legal minimum, but it\'s worth considering whether the time and effort is worthwhile for very small amounts. That said, even claims of £50-100 for minor tyre damage can be successful if you have good evidence.',
        },
        {
          question: 'What\'s the time limit for making a pothole claim?',
          answer: 'The legal time limit is 3 years from the date of the incident for property damage claims. However, you should claim as soon as possible — evidence becomes harder to gather over time, and the pothole may be repaired.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="What is a Valid Pothole Claim?"
      >
        <p className="mb-4">
          A valid pothole claim is one where you can demonstrate that a highway authority failed in their 
          legal duty to maintain a road — the duty set out in{' '}
          <Link to="/guides/section-41-explained" className={inlineLink}>
            Section 41 of the Highways Act 1980
          </Link>
          {' '}— and this failure directly caused damage to your vehicle. Not 
          every pothole incident results in compensation, but many do when properly documented.
        </p>
        <GuideTip variant="info" title="Perfect for:">
          Drivers who have suffered tyre, wheel, or suspension damage from hitting a pothole on a public 
          road and have photos and repair costs to support their claim.
        </GuideTip>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        }
        title="The Four Requirements"
      >
        <p className="mb-4">
          For a pothole claim to succeed, you need to demonstrate four things:
        </p>
        <GuideGrid cols={2}>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Public Road"
          >
            The road must be maintained by a highway authority — your local council or National Highways.{' '}
            <Link to="/guides/who-is-responsible" className={inlineLink}>
              Not sure who's responsible?
            </Link>
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Significant Defect"
          >
            The pothole must be dangerous — generally 40mm+ deep in the carriageway.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Caused Your Vehicle Damage"
          >
            You need to link hitting the pothole directly to the damage on your vehicle.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Evidence"
          >
            <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
              Photos of the pothole
            </Link>
            {' '}and{' '}
            <Link to="/guides/documenting-vehicle-damage" className={inlineLink}>
              your vehicle damage
            </Link>
            , plus a repair invoice or quote from a garage.
          </GuideCard>
        </GuideGrid>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="What Counts as a Pothole?"
      >
        <p className="text-brand-200 mb-4">
          Highway authorities use specific thresholds to determine what constitutes a dangerous defect:
        </p>
        <GuideGrid cols={3}>
          <GuideCard variant="dark" title="Depth">
            40mm or deeper (about 1.5 inches) is the typical intervention threshold
          </GuideCard>
          <GuideCard variant="dark" title="Width">
            Large enough to affect a vehicle tyre travelling normally on the road
          </GuideCard>
          <GuideCard variant="dark" title="Location">
            In the carriageway where vehicles travel, not just on verges or kerbs
          </GuideCard>
        </GuideGrid>
        <div className="bg-white/10 border border-white/20 rounded-xl p-4 mt-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5 text-white">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="text-white">
              <p className="font-semibold mb-1">Don't have a 40mm+ pothole?</p>
              <p className="text-sm leading-relaxed text-white/90">
                Shallower defects can still cause damage, especially at speed. If you have clear evidence 
                of a defect and resulting damage, it's still worth checking your eligibility.
              </p>
            </div>
          </div>
        </div>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Types of Damage You Can Claim For"
      >
        <p className="mb-4">Common types of pothole damage include:</p>
        <GuideGrid cols={2}>
          <GuideCard title="Tyre Damage">
            Punctures, sidewall bulges, blowouts, and cuts from pothole edges
          </GuideCard>
          <GuideCard title="Wheel Damage">
            Buckled, cracked, or scratched alloy wheels and steel rims
          </GuideCard>
          <GuideCard title="Suspension">
            Broken springs, damaged shock absorbers, control arm damage
          </GuideCard>
          <GuideCard title="Steering">
            Damaged track rods, ball joints, wheel bearing issues
          </GuideCard>
          <GuideCard title="Alignment">
            Tracking knocked out, pulling to one side after the incident
          </GuideCard>
          <GuideCard title="Bodywork">
            Damage from debris thrown up by the pothole impact
          </GuideCard>
        </GuideGrid>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        }
        title="What You Generally Can't Claim For"
      >
        <p className="mb-4">Councils won't typically compensate for:</p>
        <ul className="space-y-2 text-surface-700">
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span><strong>Inconvenience</strong> — Time off work, hassle of arranging repairs</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span><strong>Loss of use</strong> — Not being able to drive your car while it's repaired</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span><strong>Hire car costs</strong> — Unless absolutely necessary and proven</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span><strong>Emotional distress</strong> — This is a property damage claim, not personal injury</span>
          </li>
        </ul>
        <p className="mt-4 text-sm text-surface-600">
          Focus your claim on actual repair costs, supported by invoices or quotes from a garage.
        </p>
      </GuideSection>

      <GuideSteps>
        <GuideStep number={1} title="Check the Road Type">
          <p className="mb-2">
            Make sure the incident happened on a publicly maintained road. Private roads, car parks, 
            and unadopted roads don't qualify for claims against the council.
          </p>
          <GuideTip variant="info">
            Not sure if your road is publicly maintained? Mac can suggest a likely authority from
            the postcode, but you must verify the specific road manager through official records.
          </GuideTip>
        </GuideStep>

        <GuideStep number={2} title="Assess the Defect">
          <p className="mb-2">
            Was the pothole significant? Generally, this means 40mm+ deep in the carriageway. 
            Take photos showing the depth if possible.
          </p>
        </GuideStep>

        <GuideStep number={3} title="Document the Damage">
          <p className="mb-2">
            Get your vehicle inspected and obtain a{' '}
            <Link to="/guides/getting-repair-quotes" className={inlineLink}>
              repair quote or invoice
            </Link>
            . The garage should itemise what was damaged and the cost to fix each item.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Gather Your Evidence">
          <p className="mb-4">
            Photos of the pothole, photos of your damage, the exact location, and your repair
            documentation. The more evidence you have, the stronger your claim.
          </p>
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-5">
            <p className="font-semibold text-brand-950 mb-3">Evidence checklist — a complete claim includes:</p>
            <ul className="space-y-2">
              {[
                'Timestamped photos of the pothole showing its depth and width, with a coin, ruler, or similar object for scale',
                "GPS-tagged photos or a dropped map pin confirming the exact location on a public road",
                'An itemised garage quote or invoice that separates parts from labour',
                'An incident record: date, time, direction of travel, approximate speed, and weather conditions',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-surface-700">
                  <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </GuideStep>

        <GuideStep number={5} title="Check Eligibility with Fixtyer">
          <p>
            Use Fixtyer&apos;s free check for a preliminary evidence review and a likely-authority
            lead. It does not confirm liability or claim merit. Verify the specific road manager
            and assess third-party report leads against the authority&apos;s own records before
            deciding what to do.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideTip variant="success" title="Act Quickly">
        Time matters with pothole claims. The pothole may be repaired, evidence fades, and you 
        have a 3-year legal time limit. Start your claim as soon as possible after the incident.
      </GuideTip>
    </GuideLayout>
  );
}
